<i18n locale="en">
{
  "labels": {
    "success": "Copied value to clipboard",
    "error": "Unable to copy to clipboard"
  }
}
</i18n>

<template>
  <OwnStack
    class="copyable"
    justify="between"
    align="center"
    el="button"
    spacing="2"
    row
    :shrink="iconOnly"
    @click.stop="onClick"
  >
    <OwnType
      v-if="!iconOnly"
      class="text--truncate"
      :text="label ?? value"
      variant="monospace"
      color="inherit"
    />
    <PhClipboardText class="flex-shrink-0" size="16" />
  </OwnStack>
</template>

<script lang="ts" setup>
import { PhClipboardText } from 'phosphor-vue/dist/phosphor-vue.esm'

import { logError } from '@/logger'
import { OwnStack, OwnType } from '@/ui'
import { useI18n, useNotify } from '@/utils'
import { clipboard } from '@/utils/clipboard'

const props = withDefaults(
  defineProps<{
    iconOnly?: boolean
    label?: string
    successMessage?: string
    value: string
  }>(),
  {
    iconOnly: false,
    label: undefined,
    successMessage: undefined,
  }
)

const notify = useNotify()
const i18n = useI18n()

const onClick = () => {
  try {
    clipboard.writeToClipboard(props.value)
    notify(
      props.successMessage ?? i18n.$t('labels.success', { value: props.value }),
      'success'
    )
  } catch (err) {
    notify(i18n.$t('labels.error'), 'error')
    logError(err)
  }
}
</script>

<style lang="scss" scoped>
.copyable {
  padding: 4px;
  border: 1px dashed $background-divider;
  border-radius: 4px;
  color: $text-color-secondary;
  transition: transitionOne(border);

  &:hover {
    border: 1px solid $background-divider;
  }

  &:active {
    color: rgba($text-color-secondary, 60%);
  }
}
</style>
